import { Printer, HandCoins, CheckCircle, AlertCircle, Zap, Clock, XCircle, RefreshCw, KeyRound } from 'lucide-react';
import { Button } from '@/components/ui/button';

/**
 * Guia de Patrocínios B2B para o Parceiro.
 *
 * Props:
 * - partnerName: nome real ou genérico
 * - monthlyPrice: preço mensal (R$) que o admin definiu pra ele (ou null)
 * - autoApprove: bool — se o parceiro tem aprovação automática
 * - supportWhatsapp / supportEmail
 */
export const PartnerSponsorshipGuide = ({
  partnerName = 'Sua Empresa Parceira',
  monthlyPrice = 49.9,
  autoApprove = false,
  supportWhatsapp = '+55 (31) 99131-6297',
  supportEmail = 'falocomodono@gmail.com',
}) => {
  const handlePrint = () => window.print();
  const priceLabel = monthlyPrice ? `R$ ${Number(monthlyPrice).toFixed(2).replace('.', ',')}` : 'R$ 49,90';

  return (
    <div data-testid="partner-sponsorship-guide">
      {/* Botão imprimir */}
      <div className="flex items-center justify-between mb-6 print:hidden">
        <div>
          <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold">Guia de Patrocínios</p>
          <h2 className="text-xl sm:text-2xl font-bold text-slate-900">
            Como patrocinar clientes · {partnerName}
          </h2>
        </div>
        <Button onClick={handlePrint} className="bg-[#0A2647] hover:bg-[#0e3260] text-white gap-2" data-testid="print-sponsorship-guide-btn">
          <Printer className="w-4 h-4" /> Baixar PDF / Imprimir
        </Button>
      </div>

      {/* Conteúdo */}
      <div className="bg-white rounded-2xl shadow-lg p-8 sm:p-10 max-w-4xl mx-auto print:shadow-none print:p-0 print:max-w-none">

        <header className="mb-8">
          <span className="inline-block px-3 py-1 rounded-full bg-gradient-to-r from-[#C5A059] to-[#b8933f] text-[#0A2647] text-[11px] font-bold uppercase tracking-widest mb-3">
            ★ Guia de Patrocínios · Falo com o Dono
          </span>
          <h1 className="text-3xl sm:text-4xl font-extrabold text-[#0A2647] mb-2 leading-tight">
            Como presentear seus clientes com o Plano 5 Estrelas
          </h1>
          <p className="text-slate-600 text-base leading-relaxed">
            Um guia objetivo de <strong>como funciona</strong>, <strong>quanto custa</strong>, <strong>o que acontece</strong> ao longo do tempo e como você pode cancelar a qualquer momento.
          </p>
        </header>

        {/* Visão geral */}
        <section className="mb-10">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 rounded-full bg-[#C5A059]/20 flex items-center justify-center">
              <HandCoins className="w-4 h-4 text-[#C5A059]" />
            </div>
            <h2 className="text-xl font-bold text-[#0A2647]">O que é o Patrocínio?</h2>
          </div>
          <div className="bg-gradient-to-br from-amber-50 to-white border border-amber-200 rounded-xl p-5 space-y-3 text-sm text-slate-700 leading-relaxed">
            <p>
              Você banca uma <strong>mensalidade fixa de {priceLabel}</strong> por cliente patrocinado, e ele recebe o <strong>Plano 5 Estrelas</strong> do Falo com o Dono <strong>sem custo direto</strong> — todos os recursos liberados.
            </p>
            <p>
              É uma gentileza poderosa: você cria valor real pro seu cliente, ele fica fidelizado a você e ainda passa a usar uma ferramenta que aumenta avaliações e visibilidade do negócio dele.
            </p>
            <p className="text-xs text-amber-900 bg-amber-100/60 rounded-lg p-3">
              💡 <strong>Para o cliente, o Plano 5★ é "cortesia sua"</strong> — aparece um selo "Cortesia de {partnerName}" no painel dele. Você ganha visibilidade e gratidão direta.
            </p>
          </div>
        </section>

        {/* Modo turbo */}
        {autoApprove && (
          <section className="mb-10">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center">
                <Zap className="w-4 h-4 text-emerald-600" />
              </div>
              <h2 className="text-xl font-bold text-[#0A2647]">Modo turbo: Aprovação automática ativa</h2>
            </div>
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5 text-sm text-emerald-900 leading-relaxed">
              <p>
                Seu cadastro tem <strong>aprovação automática habilitada</strong>. Isso significa que ao patrocinar um cliente, o sistema cria a conta dele <strong>na hora</strong> e mostra as credenciais (e-mail + senha) imediatamente no seu painel — sem espera por aprovação do administrador.
              </p>
            </div>
          </section>
        )}

        {/* Passo a passo */}
        <section className="mb-10">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 rounded-full bg-[#0A2647] flex items-center justify-center">
              <CheckCircle className="w-4 h-4 text-[#C5A059]" />
            </div>
            <h2 className="text-xl font-bold text-[#0A2647]">Passo a passo: como patrocinar</h2>
          </div>
          <ol className="space-y-4">
            <Step n={1} title="Acesse a aba Patrocínios">
              No seu painel, clique em <strong>"Patrocínios"</strong> no menu superior. É onde tudo acontece.
            </Step>
            <Step n={2} title="Clique em 'Patrocinar novo cliente'">
              Você verá um botão dourado bem visível. Clique e abrirá o formulário.
            </Step>
            <Step n={3} title="Preencha os dados do cliente final">
              Informe: nome da empresa, CNPJ, nome do dono, e-mail (será o login dele), WhatsApp e a duração que você quer bancar (3, 6, 12, 24 meses ou indefinido).
            </Step>
            <Step n={4} title="Envie a solicitação">
              {autoApprove ? (
                <>Como você tem <strong>aprovação automática</strong>, a conta é criada <strong>na hora</strong>. As credenciais aparecem imediatamente.</>
              ) : (
                <>O administrador será notificado e aprovará em até 24h. Quando aprovado, as <strong>credenciais aparecem no seu painel</strong> automaticamente.</>
              )}
            </Step>
            <Step n={5} title="Entregue as credenciais ao cliente">
              Use o botão <strong>"Copiar mensagem pronta"</strong> dentro do modal de credenciais. Ele copia um texto formatado pronto pra colar no WhatsApp ou e-mail do seu cliente.
            </Step>
            <Step n={6} title="Pronto! Comece a ser cobrado">
              A primeira fatura aparece no seu Asaas em até 24h. Você escolhe pagar com PIX, cartão ou boleto.
            </Step>
          </ol>
        </section>

        {/* Regras de cobrança */}
        <section className="mb-10">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 rounded-full bg-[#0A2647] flex items-center justify-center">
              <KeyRound className="w-4 h-4 text-[#C5A059]" />
            </div>
            <h2 className="text-xl font-bold text-[#0A2647]">Como funciona a cobrança</h2>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <InfoCard title="Cobrança individual">
              Cada cliente patrocinado gera <strong>uma assinatura separada</strong> no seu Asaas, com o nome dele na descrição da fatura. Você acompanha e cancela individualmente.
            </InfoCard>
            <InfoCard title="Forma de pagamento">
              <strong>PIX, cartão de crédito ou boleto</strong> — você escolhe na hora de pagar cada fatura.
            </InfoCard>
            <InfoCard title="Valor mensal">
              Hoje: <strong>{priceLabel} por cliente, por mês</strong>. O valor é definido pelo administrador.
            </InfoCard>
            <InfoCard title="Duração">
              Você escolhe na hora do cadastro: <strong>3, 6, 12, 24 meses ou indefinido</strong> (até você cancelar).
            </InfoCard>
          </div>
        </section>

        {/* Ciclo de vida */}
        <section className="mb-10">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 rounded-full bg-[#0A2647] flex items-center justify-center">
              <Clock className="w-4 h-4 text-[#C5A059]" />
            </div>
            <h2 className="text-xl font-bold text-[#0A2647]">Ciclo de vida do patrocínio</h2>
          </div>

          <div className="space-y-4">
            <LifecycleStep color="bg-emerald-500" title="Ativo · Tudo certo">
              Cliente usando o Plano 5 Estrelas normalmente. Você recebe a fatura mensal no Asaas. No painel dele, aparece o selo "Cortesia de {partnerName}".
            </LifecycleStep>

            <LifecycleStep color="bg-blue-500" title="30 dias antes do fim · Pré-aviso">
              Você e o cliente recebem um e-mail avisando da expiração próxima. <strong>Você decide:</strong> criar um novo patrocínio (renovar) ou deixar o ciclo terminar.
            </LifecycleStep>

            <LifecycleStep color="bg-amber-500" title="7 e 1 dia antes · Avisos finais ao cliente">
              O cliente recebe e-mail e banner cada vez mais destacados, sugerindo que ele já <strong>contrate o plano direto</strong> com a gente para não perder recursos.
            </LifecycleStep>

            <LifecycleStep color="bg-rose-500" title="Dia 0 · Patrocínio encerra">
              Sua cobrança para automaticamente no Asaas (se foi por duração definida). O cliente vê um modal pedindo pra escolher um plano próprio em até 7 dias.
            </LifecycleStep>

            <LifecycleStep color="bg-slate-400" title="+7 dias · Downgrade automático">
              Se o cliente não escolher plano, ele cai pro <strong>plano Free</strong> (15 avaliações/mês). Os dados dele ficam preservados. <strong>Você ainda é o parceiro responsável dele</strong> — se ele virar pagante depois, a comissão volta pra você normalmente.
            </LifecycleStep>
          </div>
        </section>

        {/* Cancelar */}
        <section className="mb-10">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 rounded-full bg-rose-100 flex items-center justify-center">
              <XCircle className="w-4 h-4 text-rose-600" />
            </div>
            <h2 className="text-xl font-bold text-[#0A2647]">Quero cancelar um patrocínio</h2>
          </div>
          <div className="bg-rose-50 border border-rose-200 rounded-xl p-5 text-sm text-slate-700 leading-relaxed space-y-3">
            <p>
              Você cancela <strong>quando quiser, sem precisar de aprovação</strong>. Na aba Patrocínios, clique no botão "Cancelar" do patrocínio desejado e escolha:
            </p>
            <ul className="space-y-2 ml-1">
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-rose-500 flex-shrink-0 mt-2"></span>
                <span><strong>No fim do ciclo atual:</strong> você não recebe a próxima fatura. Cliente fica ativo até a fatura já paga vencer.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-rose-700 flex-shrink-0 mt-2"></span>
                <span><strong>Imediatamente:</strong> cliente cai pro Free agora. Fatura já paga não é estornada.</span>
              </li>
            </ul>
          </div>
        </section>

        {/* Inadimplência */}
        <section className="mb-10">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 rounded-full bg-rose-100 flex items-center justify-center">
              <AlertCircle className="w-4 h-4 text-rose-600" />
            </div>
            <h2 className="text-xl font-bold text-[#0A2647]">E se eu atrasar uma fatura?</h2>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 text-sm text-slate-700 leading-relaxed space-y-3">
            <p className="font-bold text-amber-900">
              ⚠️ O cliente NÃO fica sabendo do atraso. Toda comunicação vem pra você.
            </p>
            <ul className="space-y-2">
              <li><strong>3 dias de atraso:</strong> e-mail amigável avisando pra regularizar.</li>
              <li><strong>7 dias de atraso:</strong> e-mail mais firme — alertando que em breve o cliente cai pro Free.</li>
              <li><strong>15 dias de atraso:</strong> patrocínio é <strong>suspenso</strong>. O cliente cai pro Free e recebe um e-mail neutro tipo "seu patrocínio encerrou" (sem expor que houve inadimplência).</li>
            </ul>
            <div className="bg-emerald-100/80 border border-emerald-200 rounded-lg p-3 flex items-start gap-2 text-emerald-900">
              <RefreshCw className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <p><strong>Boa notícia:</strong> se você pagar a fatura atrasada depois da suspensão, <strong>o sistema reativa o cliente automaticamente</strong> pro Plano 5 Estrelas. Sem ação manual.</p>
            </div>
          </div>
        </section>

        {/* Comissões */}
        <section className="mb-10">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 rounded-full bg-[#0A2647] flex items-center justify-center">
              <CheckCircle className="w-4 h-4 text-[#C5A059]" />
            </div>
            <h2 className="text-xl font-bold text-[#0A2647]">E as comissões nesse caso?</h2>
          </div>
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-5 text-sm text-slate-700 leading-relaxed space-y-2">
            <p>
              Como você já está bancando o cliente, <strong>não há comissão de implantação</strong> para este cliente patrocinado (nem afiliados padrão nem master ganham).
            </p>
            <p>
              Mas você <strong>continua sendo o parceiro responsável dele para sempre</strong>. Se daqui a um tempo ele virar pagante orgânico (contratar plano próprio), a comissão recorrente volta pra você normalmente.
            </p>
            <p>
              Se uma <strong>nova empresa</strong> chegar pela indicação do cliente patrocinado (boca a boca ou vendo o QR Code dele), essa nova empresa é tratada normalmente — você recebe comissão se trouxe ela diretamente.
            </p>
          </div>
        </section>

        {/* Dúvidas */}
        <section>
          <div className="bg-gradient-to-br from-[#0A2647] to-[#0e3260] text-white rounded-xl p-6 print:bg-slate-100 print:text-slate-900">
            <h3 className="text-base font-bold mb-2">Dúvidas? Conte com a gente.</h3>
            <p className="text-sm opacity-90 mb-3">Nossa equipe te apoia em cada etapa do processo.</p>
            <div className="text-sm space-y-1">
              <p>📱 WhatsApp: <strong>{supportWhatsapp}</strong></p>
              <p>✉️ E-mail: <strong>{supportEmail}</strong></p>
            </div>
          </div>
        </section>

        <footer className="text-center text-xs text-slate-400 mt-10 pt-6 border-t border-slate-200">
          Falo com o Dono · Guia de Patrocínios B2B · v1.0
        </footer>
      </div>
    </div>
  );
};

const Step = ({ n, title, children }) => (
  <li className="flex gap-4">
    <div className="w-9 h-9 rounded-full bg-[#C5A059] text-[#0A2647] font-extrabold flex items-center justify-center flex-shrink-0">
      {n}
    </div>
    <div className="pt-1.5">
      <p className="font-bold text-[#0A2647] text-sm">{title}</p>
      <p className="text-sm text-slate-600 leading-relaxed mt-1">{children}</p>
    </div>
  </li>
);

const InfoCard = ({ title, children }) => (
  <div className="border border-slate-200 rounded-xl p-4 bg-white">
    <p className="text-xs font-bold text-[#C5A059] uppercase tracking-wider mb-2">{title}</p>
    <p className="text-sm text-slate-700 leading-relaxed">{children}</p>
  </div>
);

const LifecycleStep = ({ color, title, children }) => (
  <div className="flex gap-3">
    <div className={`w-3 h-3 rounded-full ${color} mt-1.5 flex-shrink-0`}></div>
    <div className="flex-1 pb-3 border-l-2 border-slate-100 pl-4 -ml-[7px]">
      <p className="font-bold text-[#0A2647] text-sm">{title}</p>
      <p className="text-sm text-slate-600 leading-relaxed mt-1">{children}</p>
    </div>
  </div>
);

export default PartnerSponsorshipGuide;
